import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { parseDateValue } from './formats';
import { CalendarConfig, ColumnDef } from './types';

interface CalendarViewProps<T extends { id: string }> {
  rows: T[];
  columns: ColumnDef<T>[];
  visibleColumns: ColumnDef<T>[];
  config: CalendarConfig | undefined;
  titleColumnId?: string;
  onRowClick?: (row: T) => void;
}

const MS_PER_DAY = 86_400_000;
const DOW_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Range of weeks rendered, relative to today. Should cover the realistic
// scroll horizon for production schedules. Cheap to make larger if needed —
// each day cell is small.
const WEEKS_BEFORE_TODAY = 12;
const WEEKS_AFTER_TODAY = 26;

function dayMs(d: Date): number {
  return Math.floor(d.getTime() / MS_PER_DAY) * MS_PER_DAY;
}

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay()); // Sunday start
  return out;
}

export function CalendarView<T extends { id: string }>({
  rows,
  columns,
  visibleColumns,
  config,
  titleColumnId,
  onRowClick,
}: CalendarViewProps<T>) {
  const showWeekends = config?.showWeekends !== false;

  const dateColumn = useMemo(() => {
    const id = config?.dateColumnId;
    if (!id) return columns.find((c) => c.dataType === 'date') ?? null;
    return columns.find((c) => c.id === id) ?? null;
  }, [columns, config]);

  const pillColumns = useMemo(() => {
    const exclude = new Set<string>();
    if (dateColumn) exclude.add(dateColumn.id);
    return visibleColumns.filter((c) => !exclude.has(c.id));
  }, [visibleColumns, dateColumn]);

  const titleColumn = useMemo(() => {
    const explicit = columns.find((c) => c.id === titleColumnId);
    if (explicit && pillColumns.includes(explicit)) return explicit;
    return pillColumns[0] ?? columns[0];
  }, [columns, titleColumnId, pillColumns]);

  const subColumns = useMemo(
    () => pillColumns.filter((c) => c.id !== titleColumn?.id),
    [pillColumns, titleColumn]
  );

  const buckets = useMemo(() => {
    const map = new Map<number, T[]>();
    if (!dateColumn) return map;
    for (const r of rows) {
      const ts = dateColumn.numericValue
        ? dateColumn.numericValue(r)
        : parseDateValue(dateColumn.accessor(r));
      if (!Number.isFinite(ts)) continue;
      const k = dayMs(new Date(ts));
      const arr = map.get(k);
      if (arr) arr.push(r);
      else map.set(k, [r]);
    }
    return map;
  }, [rows, dateColumn]);

  const weeks = useMemo(() => {
    const todayStart = startOfWeek(new Date());
    const first = new Date(todayStart);
    first.setDate(first.getDate() - WEEKS_BEFORE_TODAY * 7);
    const out: Date[][] = [];
    for (let w = 0; w < WEEKS_BEFORE_TODAY + 1 + WEEKS_AFTER_TODAY; w++) {
      const row: Date[] = [];
      for (let d = 0; d < 7; d++) {
        if (!showWeekends && (d === 0 || d === 6)) continue;
        const day = new Date(first);
        day.setDate(day.getDate() + w * 7 + d);
        row.push(day);
      }
      out.push(row);
    }
    return out;
  }, [showWeekends]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const todayRef = useRef<HTMLDivElement | null>(null);

  // Scroll to today on initial mount and whenever weekends toggle changes the layout.
  useLayoutEffect(() => {
    if (!todayRef.current || !scrollRef.current) return;
    const node = todayRef.current;
    const parent = scrollRef.current;
    parent.scrollTop = node.offsetTop - parent.clientHeight / 3;
  }, [showWeekends]);

  const todayDayMs = dayMs(new Date());

  const scrollToToday = () => {
    if (!todayRef.current || !scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: todayRef.current.offsetTop - scrollRef.current.clientHeight / 3,
      behavior: 'smooth',
    });
  };

  const dows = showWeekends ? DOW_FULL : DOW_FULL.slice(1, 6);
  const colCount = dows.length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col h-full min-h-[280px]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50/60 shrink-0">
        <button
          onClick={scrollToToday}
          className="px-2 h-7 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Jump to today
        </button>
        <div className="text-[11px] text-slate-400">
          {dateColumn ? `by ${dateColumn.label}` : 'no date column'}
        </div>
      </div>

      <div
        className="grid bg-slate-50/30 border-b border-slate-200 shrink-0"
        style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
      >
        {dows.map((d) => (
          <div
            key={d}
            className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 text-center"
          >
            {d}
          </div>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        <div
          className="grid auto-rows-[6.5rem]"
          style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
        >
          {weeks.flatMap((week) =>
            week.map((day) => {
              const key = dayMs(day);
              const isToday = key === todayDayMs;
              const items = buckets.get(key) ?? [];
              const isFirstOfMonth = day.getDate() === 1;
              return (
                <div
                  key={day.getTime()}
                  ref={isToday ? todayRef : undefined}
                  className={cn(
                    'border-r border-b border-slate-100 last:border-r-0 p-1 overflow-hidden flex flex-col gap-0.5',
                    isToday && 'bg-blue-50/20'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'text-[10px] font-medium inline-flex items-center gap-1 px-1',
                        isToday
                          ? 'inline-flex items-center justify-center w-5 h-5 px-0 rounded-full bg-slate-900 text-white'
                          : 'text-slate-700'
                      )}
                    >
                      {isFirstOfMonth && !isToday && (
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                          {day.toLocaleDateString(undefined, { month: 'short' })}
                        </span>
                      )}
                      {day.getDate()}
                    </span>
                    {items.length > 0 && (
                      <span className="text-[10px] text-slate-400">{items.length}</span>
                    )}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-0.5">
                    {items.map((row) => (
                      <button
                        key={row.id}
                        onClick={() => onRowClick?.(row)}
                        className={cn(
                          'w-full text-left px-1.5 py-0.5 rounded text-[11px]',
                          onRowClick
                            ? 'bg-blue-50 text-blue-800 hover:bg-blue-100 cursor-pointer'
                            : 'bg-slate-100 text-slate-700'
                        )}
                      >
                        <div className="font-medium truncate">
                          {titleColumn ? titleColumn.accessor(row) : row.id}
                        </div>
                        {subColumns.length > 0 && (
                          <div className="text-[10px] text-slate-500 truncate leading-tight">
                            {subColumns
                              .map((c) => c.accessor(row))
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// Suppress unused-import warning if React strict mode tweaks remove useEffect later.
void useEffect;
